import React, { useState, useMemo, useCallback, useEffect } from 'react';
import {
  Box,
  Typography,
  Button,
  Select,
  MenuItem,
  FormControl,
  Link,
  Grid,
  CircularProgress,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import CustomCheckbox from '../../../common/components/CustomCheckbox/CustomCheckbox';
import { Sidebar } from '../../../common/components/Sidebar';
import SectionBar from '../../../common/components/SectionBar/SectionBar';
import { NoDataFound } from '../../../common/components/NoDataFound';
import { useNavItems } from '../../../hooks';
import { useModuleOperators } from '../../../hooks/useModuleOperators';
import { useE55Comparison, type MailDataStructure, type OperatorData, type MailItem } from './hooks';
import SortedE55ComparisonTable from './components/E55ComparisonTable';
import Table from '../../../common/components/Table/Table';
import NotificationSnackbar from '../../../common/components/Snackbar/Snackbar';
import {
  MainContent,
  ReportContainer,
  SectionTitle,
  CheckboxGroup,
  ButtonContainer,
  FilterSection,
  SelectContainer,
  StyledSegmentedControl,
  StyledSegmentButton,
  ClearResultsButton,
  ResetButton,
  ExportButton,
} from './E55Comparison.styled';

interface SamplingRelevantState {
  UA: boolean;
  UE: boolean;
  UL: boolean;
  UN: boolean;
  UY: boolean;
}

interface NotSamplingRelevantState {
  UI: boolean;
  UM: boolean;
  UP: boolean;
  UR: boolean;
  UX: boolean;
  UZ: boolean;
}

interface DirectionOption {
  id: string;
  label: string;
}

// Utility function to compare year-quarter combinations
const compareYearQuarter = (year1: string, quarter1: string, year2: string, quarter2: string): number => {
  const y1 = parseInt(year1);
  const y2 = parseInt(year2);
  const q1 = parseInt(quarter1.replace('Q', ''));
  const q2 = parseInt(quarter2.replace('Q', ''));
  
  if (y1 !== y2) {
    return y1 - y2; // Compare years first
  }
  return q1 - q2; // If years are equal, compare quarters
};

function E55Comparison() {
  const [sidebarOpen, setSidebarOpen] = useState<boolean>(true);
  const navigate = useNavigate();
  const navItems = useNavItems();
  const [searchParams, setSearchParams] = useSearchParams();

  const [ppoValue, setPpoValue] = useState('');
  const [ppoFilter, setPpoFilter] = useState('Inbound');
  const [fromYear, setFromYear] = useState('');
  const [fromQuarter, setFromQuarter] = useState('');
  const [toYear, setToYear] = useState('');
  const [toQuarter, setToQuarter] = useState('');

  // Add state for HTML results
  const [htmlResults, setHtmlResults] = useState<any[] | null>(null);
  // Add state to track if we have empty results (successful response but no data)
  const [hasEmptyResults, setHasEmptyResults] = useState<boolean>(false);
  // Add state to store the direction used when generating results
  const [resultsDirection, setResultsDirection] = useState<'Inbound' | 'Outbound'>('Inbound');

  // Snackbar state
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'error' as 'error' | 'warning' | 'info' | 'success',
  });

  // Get effective operators and cascading logic for eno1 module
  const {
    operators,
    availableYears,
    operatorsForYearRange,
    yearsForOperator,
    relationsData: allRelationsData,
    isPpoLoading,
    ppoError,
  } = useModuleOperators('eno1', { fromYear, toYear, selectedPpo: ppoValue || undefined });

  // E55 Comparison hook
  const {
    mutate: generateReport,
    isPending: isGeneratingReport,
    error: reportError,
    reset: resetReportError,
  } = useE55Comparison();

  // Map the target DOs to PPO options with cascading logic
  const ppoOptions = React.useMemo(() => {
    const options: { id: string; label: string; hasLicense?: boolean }[] = [];
    const targetDOsToUse = (fromYear || toYear) ? operatorsForYearRange : operators;
    targetDOsToUse.forEach((targetDO) => {
      options.push({ id: targetDO, label: targetDO, hasLicense: true });
    });
    return options;
  }, [operators, operatorsForYearRange, fromYear, toYear]);

  // Generate year-quarter options with cascading logic
  const yearQuarterOptions = React.useMemo(() => {
    const options: { value: string; label: string; year: string; quarter: string }[] = [];
    const quarters = ['Q1', 'Q2', 'Q3', 'Q4'];
    const yearsToShow = ppoValue ? yearsForOperator : availableYears;
    yearsToShow.forEach((year) => {
      quarters.forEach((quarter) => {
        const value = `${year}-${quarter}`;
        const label = `${year} ${quarter}`;
        options.push({ value, label, year: year.toString(), quarter });
      });
    });
    return options;
  }, [availableYears, yearsForOperator, ppoValue]);

  // Get current from and to values for display
  const getFromValue = () => {
    if (fromYear && fromQuarter) {
      return `${fromYear}-${fromQuarter}`;
    }
    return '';
  };

  const getToValue = () => {
    if (toYear && toQuarter) {
      return `${toYear}-${toQuarter}`;
    }
    return '';
  };

  // Handle from dropdown change
  const handleFromChange = (value: string) => {
    if (value) {
      const option = yearQuarterOptions.find((opt) => opt.value === value);
      if (option) {
        // Check if selected from date is later than current to date
        if (toYear && toQuarter) {
          const comparison = compareYearQuarter(option.year, option.quarter, toYear, toQuarter);
          if (comparison > 0) {
            showSnackbar('From date cannot be later than To date. Please select a valid date range.', 'warning');
            return; // Don't update the state if invalid
          }
        }
        setFromYear(option.year);
        setFromQuarter(option.quarter);
      }
    } else {
      setFromYear('');
      setFromQuarter('');
    }
  };

  // Handle to dropdown change
  const handleToChange = (value: string) => {
    if (value) {
      const option = yearQuarterOptions.find((opt) => opt.value === value);
      if (option) {
        // Check if selected to date is earlier than current from date
        if (fromYear && fromQuarter) {
          const comparison = compareYearQuarter(fromYear, fromQuarter, option.year, option.quarter);
          if (comparison > 0) {
            showSnackbar('To date cannot be earlier than From date. Please select a valid date range.', 'warning');
            return; // Don't update the state if invalid
          }
        }
        
        // Only clear DO selection if the currently selected DO is not available for the new year range
        if (toYear !== option.year && ppoValue) {
          // Check if the current DO is available for the new year range
          const relationsForCurrentDO = allRelationsData?.byTargetDO?.[ppoValue];
          if (!relationsForCurrentDO) {
            setPpoValue('');
            setToYear(option.year);
            setToQuarter(option.quarter);
            return;
          }
          
          // Get years where this DO has eno1 module enabled
          const availableYears = relationsForCurrentDO
            .filter(relation => 
              relation.modules.some(module => 
                module.name === 'eno1' && module.enabled === true
              )
            )
            .map(relation => relation.year)
            .sort((a, b) => a - b);
          
          if (availableYears.length === 0) {
            setPpoValue('');
            setToYear(option.year);
            setToQuarter(option.quarter);
            return;
          }
          
          const minYear = availableYears[0];
          const maxYear = availableYears[availableYears.length - 1];
          const newToYearNum = parseInt(option.year);
          
          // Check if the new to year is within the DO's range
          let shouldClearDO = false;
          
          if (fromYear) {
            // Both from and to are selected - check if the range overlaps with DO's range
            const fromYearNum = parseInt(fromYear);
            shouldClearDO = !(maxYear >= fromYearNum && minYear <= newToYearNum);
          } else {
            // Only to year is selected - check if DO has data up to that year
            shouldClearDO = minYear > newToYearNum;
          }
          
          if (shouldClearDO) {
            setPpoValue('');
          }
        }
        
        setToYear(option.year);
        setToQuarter(option.quarter);
      }
    } else {
      setToYear('');
      setToQuarter('');
    }
  };

  // Sampling Relevant MSCs state
  const [samplingRelevant, setSamplingRelevant] = useState<SamplingRelevantState>({
    UA: true,
    UE: true,
    UL: true,
    UN: true,
    UY: true,
  });

  // Not Sampling Relevant MSCs state
  const [notSamplingRelevant, setNotSamplingRelevant] = useState<NotSamplingRelevantState>({
    UI: false,
    UM: false,
    UP: false,
    UR: false,
    UX: false,
    UZ: false,
  });

  // Direction filter options
  const directionOptions: DirectionOption[] = [
    { id: 'Inbound', label: 'Inbound' },
    { id: 'Outbound', label: 'Outbound' },
  ];

  const handleSidebarToggle = () => {
    setSidebarOpen((prev) => !prev);
  };

  const handleSubtitleClick = () => {
    navigate('/eno1');
  };

  const handleDirectionChange = (id: string) => {
    setPpoFilter(id);
  };

  // Handle DO selection change with cascading behavior
  const handleDOChange = (value: string) => {
    // Only clear year selection if the currently selected year range is not available for the new DO
    if (ppoValue !== value && (fromYear || toYear)) {
      // Check if the current year range is available for the new DO
      const relationsForCurrentDO = allRelationsData?.byTargetDO?.[value];
      if (!relationsForCurrentDO) {
        setFromYear('');
        setFromQuarter('');
        setToYear('');
        setToQuarter('');
        setPpoValue(value);
        return;
      }
      
      // Get years where this DO has eno1 module enabled
      const availableYears = relationsForCurrentDO
        .filter(relation => 
          relation.modules.some(module => 
            module.name === 'eno1' && module.enabled === true
          )
        )
        .map(relation => relation.year)
        .sort((a, b) => a - b);
      
      if (availableYears.length === 0) {
        setFromYear('');
        setFromQuarter('');
        setToYear('');
        setToQuarter('');
        setPpoValue(value);
        return;
      }
      
      const minYear = availableYears[0];
      const maxYear = availableYears[availableYears.length - 1];
      
      let shouldClearYears = false;
      
      if (fromYear && toYear) {
        // Both from and to are selected - check if the range overlaps with DO's range
        const fromYearNum = parseInt(fromYear);
        const toYearNum = parseInt(toYear);
        shouldClearYears = !(maxYear >= fromYearNum && minYear <= toYearNum);
      } else if (fromYear) {
        // Only from year is selected - check if DO has data from that year onwards
        const fromYearNum = parseInt(fromYear);
        shouldClearYears = maxYear < fromYearNum;
      } else if (toYear) {
        // Only to year is selected - check if DO has data up to that year
        const toYearNum = parseInt(toYear);
        shouldClearYears = minYear > toYearNum;
      }
      
      if (shouldClearYears) {
        setFromYear('');
        setFromQuarter('');
        setToYear('');
        setToQuarter('');
      }
    }
    setPpoValue(value);
  };

  // Handle year change with cascading behavior
  const handleFromChangeWithCascading = (value: string) => {
    if (value) {
      const option = yearQuarterOptions.find((opt) => opt.value === value);
      if (option) {
        // Check if selected from date is later than current to date
        if (toYear && toQuarter) {
          const comparison = compareYearQuarter(option.year, option.quarter, toYear, toQuarter);
          if (comparison > 0) {
            showSnackbar('From date cannot be later than To date. Please select a valid date range.', 'warning');
            return; // Don't update the state if invalid
          }
        }
        
        // Only clear DO selection if the currently selected DO is not available for the new year range
        if (fromYear !== option.year && ppoValue) {
          // Check if the current DO is available for the new year range
          const relationsForCurrentDO = allRelationsData?.byTargetDO?.[ppoValue];
          if (!relationsForCurrentDO) {
            setPpoValue('');
            setFromYear(option.year);
            setFromQuarter(option.quarter);
            return;
          }
          
          // Get years where this DO has eno1 module enabled
          const availableYears = relationsForCurrentDO
            .filter(relation => 
              relation.modules.some(module => 
                module.name === 'eno1' && module.enabled === true
              )
            )
            .map(relation => relation.year)
            .sort((a, b) => a - b);
          
          if (availableYears.length === 0) {
            setPpoValue('');
            setFromYear(option.year);
            setFromQuarter(option.quarter);
            return;
          }
          
          const minYear = availableYears[0];
          const maxYear = availableYears[availableYears.length - 1];
          const newFromYearNum = parseInt(option.year);
          
          // Check if the new from year is within the DO's range
          let shouldClearDO = false;
          
          if (toYear) {
            // Both from and to are selected - check if the range overlaps with DO's range
            const toYearNum = parseInt(toYear);
            shouldClearDO = !(maxYear >= newFromYearNum && minYear <= toYearNum);
          } else {
            // Only from year is selected - check if DO has data from that year onwards
            shouldClearDO = maxYear < newFromYearNum;
          }
          
          if (shouldClearDO) {
            setPpoValue('');
          }
        }
        
        setFromYear(option.year);
        setFromQuarter(option.quarter);
      }
    } else {
      setFromYear('');
      setFromQuarter('');
    }
  };

  const handleSnackbarClose = (event?: React.SyntheticEvent | Event, reason?: string) => {
    if (reason === 'clickaway') {
      return;
    }
    setSnackbar((prev) => ({ ...prev, open: false }));
  };

  const showSnackbar = (
    message: string,
    severity: 'error' | 'warning' | 'info' | 'success' = 'error'
  ) => {
    setSnackbar({
      open: true,
      message,
      severity,
    });
  };

  const handleSamplingRelevantChange =
    (field: keyof SamplingRelevantState) => (event: React.ChangeEvent<HTMLInputElement>) => {
      setSamplingRelevant((prev) => ({
        ...prev,
        [field]: event.target.checked,
      }));
    };

  const handleNotSamplingRelevantChange =
    (field: keyof NotSamplingRelevantState) => (event: React.ChangeEvent<HTMLInputElement>) => {
      setNotSamplingRelevant((prev) => ({
        ...prev,
        [field]: event.target.checked,
      }));
    };

  const handleSelectAllSamplingRelevant = () => {
    const allSelected =
      samplingRelevant.UA &&
      samplingRelevant.UE &&
      samplingRelevant.UL &&
      samplingRelevant.UN &&
      samplingRelevant.UY;

    if (allSelected) {
      // Deselect all
      setSamplingRelevant({
        UA: false,
        UE: false,
        UL: false,
        UN: false,
        UY: false,
      });
    } else {
      // Select all
      setSamplingRelevant({
        UA: true,
        UE: true,
        UL: true,
        UN: true,
        UY: true,
      });
    }
  };

  const handleSelectAllNotSamplingRelevant = () => {
    const allSelected =
      notSamplingRelevant.UI &&
      notSamplingRelevant.UM &&
      notSamplingRelevant.UP &&
      notSamplingRelevant.UR &&
      notSamplingRelevant.UX &&
      notSamplingRelevant.UZ;

    if (allSelected) {
      // Deselect all
      setNotSamplingRelevant({
        UI: false,
        UM: false,
        UP: false,
        UR: false,
        UX: false,
        UZ: false,
      });
    } else {
      // Select all
      setNotSamplingRelevant({
        UI: true,
        UM: true,
        UP: true,
        UR: true,
        UX: true,
        UZ: true,
      });
    }
  };

  const handleReset = () => {
    setPpoValue('');
    setPpoFilter('Inbound');
    setFromYear('');
    setFromQuarter('');
    setToYear('');
    setToQuarter('');
    setSamplingRelevant({
      UA: true,
      UE: true,
      UL: true,
      UN: true,
      UY: true,
    });
    setNotSamplingRelevant({
      UI: true,
      UM: true,
      UP: true,
      UR: true,
      UX: true,
      UZ: true,
    });
    // Clear no data found state and results when resetting
    setHasEmptyResults(false);
    setHtmlResults(null);
    setResultsDirection('Inbound'); // Reset direction to default
  };

  // Map checkbox states to API parameter values
  const getSamplingRelevantValues = (): string[] => {
    const values: string[] = [];
    if (samplingRelevant.UA) values.push('UA');
    if (samplingRelevant.UE) values.push('UE');
    if (samplingRelevant.UL) values.push('UL');
    if (samplingRelevant.UN) values.push('UN');
    if (samplingRelevant.UY) values.push('UY');
    return values;
  };

  const getNotSamplingRelevantValues = (): string[] => {
    const values: string[] = [];
    if (notSamplingRelevant.UI) values.push('UI');
    if (notSamplingRelevant.UM) values.push('UM');
    if (notSamplingRelevant.UP) values.push('UP');
    if (notSamplingRelevant.UR) values.push('UR');
    if (notSamplingRelevant.UX) values.push('UX');
    if (notSamplingRelevant.UZ) values.push('UZ');
    return values;
  };



  // Function to apply current filters to SectionBar (URL params)
  const applyFiltersToSectionBar = () => {
    const newParams = new URLSearchParams();

    // Map PPO filter - now required
    if (ppoValue) {
      newParams.append('ppo', ppoValue);
    }

    // Add custom E55Comparison-specific filters
    const samplingRelevantValues = getSamplingRelevantValues();
    const notSamplingRelevantValues = getNotSamplingRelevantValues();

    if (samplingRelevantValues.length > 0) {
      newParams.append('samplingRelevant', samplingRelevantValues.join(','));
    }

    if (notSamplingRelevantValues.length > 0) {
      newParams.append('notSamplingRelevant', notSamplingRelevantValues.join(','));
    }

    if (fromYear && fromQuarter) {
      newParams.append('fromYear', fromYear);
      newParams.append('fromQuarter', fromQuarter);
      newParams.append('fromPeriod', `${fromYear}-${fromQuarter}`);
    }

    if (toYear && toQuarter) {
      newParams.append('toYear', toYear);
      newParams.append('toQuarter', toQuarter);
      newParams.append('toPeriod', `${toYear}-${toQuarter}`);
    }

    setSearchParams(newParams);
  };

  const handleExport = (format: 'HTML' | 'PDF' | 'XLS') => {
    // Reset any previous errors and empty results state
    resetReportError();
    setHasEmptyResults(false);

    // Validate required fields - DO is required
    if (!ppoValue) {
      showSnackbar('Please select a Designated Operator (DO)');
      return;
    }

    // Validate required fields - From and To are now required
    if (!fromYear || !fromQuarter) {
      showSnackbar('Please select a From period (Year and Quarter)');
      return;
    }

    if (!toYear || !toQuarter) {
      showSnackbar('Please select a To period (Year and Quarter)');
      return;
    }

    // Validate required fields
    const samplingRelevantValues = getSamplingRelevantValues();
    const notSamplingRelevantValues = getNotSamplingRelevantValues();

    if (samplingRelevantValues.length === 0 && notSamplingRelevantValues.length === 0) {
      showSnackbar('Please select at least one MSC code');
      return;
    }

    // Prepare API parameters
    const params = {
      formatType: format.toLowerCase() as 'html' | 'pdf' | 'xls',
      fromYear: parseInt(fromYear),
      fromQuarter: parseInt(fromQuarter.replace('Q', '')),
      toYear: parseInt(toYear),
      toQuarter: parseInt(toQuarter.replace('Q', '')),
      ...(samplingRelevantValues.length > 0 && { samplingRelevant: samplingRelevantValues }),
      ...(notSamplingRelevantValues.length > 0 && { notSamplingRelevant: notSamplingRelevantValues }),
      ...(ppoValue && { ppo: ppoValue }),
      ...(ppoFilter && { inboundOrOutboundFilter: ppoFilter }),
    };

    generateReport(params, {
      onSuccess: (response) => {
        if (format === 'HTML') {
          // For HTML, display the tables data received from the API
          console.log('HTML E55 Comparison tables generated:', response.data);
          if (response.data) {
            // Check if the data is empty or contains no meaningful results
            const isEmpty = !response.data || 
                           (Array.isArray(response.data) && response.data.length === 0) ||
                           (Array.isArray(response.data) && response.data.every((period: any) => 
                             !period.compareResult || period.compareResult.length === 0
                           ));
            
            if (isEmpty) {
              setHasEmptyResults(true);
              setHtmlResults(null);
              setResultsDirection('Inbound'); // Reset to default
            } else {
              setHtmlResults(response.data);
              setHasEmptyResults(false);
              setResultsDirection(ppoFilter as 'Inbound' | 'Outbound'); // Store the direction used
              // Apply current filters to SectionBar when HTML is generated
              applyFiltersToSectionBar();
            }
          } else {
            setHasEmptyResults(true);
            setHtmlResults(null);
            setResultsDirection('Inbound'); // Reset to default
          }
        } else {
          // For PDF/XLS, download is handled automatically in the hook
          console.log(
            `${format} E55 Comparison downloaded successfully with filename: ${response.filename}`
          );
        }
      },
      onError: async (error) => {
        console.error('Error generating report:', error);
        
        let errorMessage = 'An error occurred while generating the report';
        let errorStatus: string | undefined;
        
        // Check if it's an axios error
        if (axios.isAxiosError(error)) {
          // For blob responses, the error data might be a Blob containing JSON
          if (error.response?.data instanceof Blob) {
            try {
              const text = await error.response.data.text();
              const errorData = JSON.parse(text);
              errorMessage = errorData.message || errorMessage;
              errorStatus = errorData.status;
            } catch (parseError) {
              // If parsing fails, use default message
              console.error('Failed to parse error response:', parseError);
            }
          } else if (error.response?.data && typeof error.response.data === 'object') {
            // For non-blob responses, check the data directly
            const errorData = error.response.data as { message?: string; status?: string };
            errorMessage = errorData.message || errorMessage;
            errorStatus = errorData.status;
          }
          
          // Check status code as fallback
          if (!errorStatus && error.response?.status === 404) {
            errorStatus = 'NOT_FOUND';
          }
        }
        
        // Show snackbar with appropriate message for all formats (HTML, XLS)
        if (errorStatus === 'NOT_FOUND' || errorMessage.includes('No e55 comparison') || errorMessage.includes('No E55')) {
          showSnackbar(
            errorMessage || 'No e55 comparison reports available for the selected criteria',
            'warning'
          );
        } else {
          showSnackbar(errorMessage, 'error');
        }
        
        setHasEmptyResults(false);
      },
    });
  };



  // Sync URL params with page state when SectionBar filters change (only when results exist)
  useEffect(() => {
    const ppoParam = searchParams.get('ppo');

    // Only sync if we have HTML results (filters are active)
    if (htmlResults) {
      if (ppoParam && ppoParam !== ppoValue) {
        setPpoValue(ppoParam === 'All' ? '' : ppoParam);
      }
    }
  }, [searchParams, htmlResults, ppoValue]);

  // Remove auto-regeneration - users must click export buttons manually

  // Memoize clearResults callback
  const clearResults = useCallback(() => {
    setHtmlResults(null);
    setHasEmptyResults(false);
    setResultsDirection('Inbound'); // Reset direction to default
    // Clear URL parameters when results are cleared
    setSearchParams(new URLSearchParams());
  }, [setSearchParams]);

  // Helper functions to determine button text
  const getSamplingRelevantButtonText = () => {
    const allSelected =
      samplingRelevant.UA &&
      samplingRelevant.UE &&
      samplingRelevant.UL &&
      samplingRelevant.UN &&
      samplingRelevant.UY;
    return allSelected ? 'Deselect all' : 'Select all';
  };

  const getNotSamplingRelevantButtonText = () => {
    const allSelected =
      notSamplingRelevant.UI &&
      notSamplingRelevant.UM &&
      notSamplingRelevant.UP &&
      notSamplingRelevant.UR &&
      notSamplingRelevant.UX &&
      notSamplingRelevant.UZ;
    return allSelected ? 'Deselect all' : 'Select all';
  };

  // Handler functions for SectionBar custom filter changes
  const handleSectionBarSamplingRelevantChange = useCallback((values: string[]) => {
    setSamplingRelevant({
      UA: values.includes('UA'),
      UE: values.includes('UE'),
      UL: values.includes('UL'),
      UN: values.includes('UN'),
      UY: values.includes('UY'),
    });
  }, []);

  const handleSectionBarNotSamplingRelevantChange = useCallback((values: string[]) => {
    setNotSamplingRelevant({
      UI: values.includes('UI'),
      UM: values.includes('UM'),
      UP: values.includes('UP'),
      UR: values.includes('UR'),
      UX: values.includes('UX'),
      UZ: values.includes('UZ'),
    });
  }, []);

  const handleSectionBarFromPeriodChange = useCallback(
    (period: string) => {
      if (period) {
        const option = yearQuarterOptions.find((opt) => opt.value === period);
        if (option) {
          setFromYear(option.year);
          setFromQuarter(option.quarter);
          
          // Update URL parameters
          const newParams = new URLSearchParams(searchParams);
          newParams.delete('fromYear');
          newParams.delete('fromQuarter');
          newParams.delete('fromPeriod');
          newParams.append('fromYear', option.year);
          newParams.append('fromQuarter', option.quarter);
          newParams.append('fromPeriod', period);
          setSearchParams(newParams);
        }
      } else {
        setFromYear('');
        setFromQuarter('');
        
        // Update URL parameters to clear from period
        const newParams = new URLSearchParams(searchParams);
        newParams.delete('fromYear');
        newParams.delete('fromQuarter');
        newParams.delete('fromPeriod');
        setSearchParams(newParams);
      }
    },
    [yearQuarterOptions, searchParams, setSearchParams]
  );

  const handleSectionBarToPeriodChange = useCallback(
    (period: string) => {
      if (period) {
        const option = yearQuarterOptions.find((opt) => opt.value === period);
        if (option) {
          setToYear(option.year);
          setToQuarter(option.quarter);
          
          // Update URL parameters
          const newParams = new URLSearchParams(searchParams);
          newParams.delete('toYear');
          newParams.delete('toQuarter');
          newParams.delete('toPeriod');
          newParams.append('toYear', option.year);
          newParams.append('toQuarter', option.quarter);
          newParams.append('toPeriod', period);
          setSearchParams(newParams);
        }
      } else {
        setToYear('');
        setToQuarter('');
        
        // Update URL parameters to clear to period
        const newParams = new URLSearchParams(searchParams);
        newParams.delete('toYear');
        newParams.delete('toQuarter');
        newParams.delete('toPeriod');
        setSearchParams(newParams);
      }
    },
    [yearQuarterOptions, searchParams, setSearchParams]
  );

  // Handler function for SectionBar direction change
  const handleSectionBarDirectionChange = useCallback((direction: string) => {
    setPpoFilter(direction);
    // Note: direction is not saved to URL for this report to avoid conflicts
  }, []);

  // Create custom filters object for SectionBar
  const customFiltersForSectionBar = useMemo(() => {
    if (!htmlResults) return undefined;

    return {
      samplingRelevant: getSamplingRelevantValues(),
      notSamplingRelevant: getNotSamplingRelevantValues(),
      fromPeriod: getFromValue(),
      toPeriod: getToValue(),
      direction: ppoFilter,
      onMSCChange: (values: string[]) => {
        const samplingRelevantCodes = ['UA', 'UE', 'UL', 'UN', 'UY'];
        const notSamplingRelevantCodes = ['UI', 'UM', 'UP', 'UR', 'UX', 'UZ'];
        
        handleSectionBarSamplingRelevantChange(values.filter(v => samplingRelevantCodes.includes(v)));
        handleSectionBarNotSamplingRelevantChange(values.filter(v => notSamplingRelevantCodes.includes(v)));
      },
      onFromPeriodChange: handleSectionBarFromPeriodChange,
      onToPeriodChange: handleSectionBarToPeriodChange,
      onDirectionChange: handleSectionBarDirectionChange,
      onExport: handleExport,
      isExporting: isGeneratingReport,
    };
  }, [
    htmlResults,
    samplingRelevant,
    notSamplingRelevant,
    fromYear,
    fromQuarter,
    toYear,
    toQuarter,
    ppoFilter,
    getFromValue,
    getToValue,
    searchParams,
    setSearchParams,
    handleSectionBarSamplingRelevantChange,
    handleSectionBarNotSamplingRelevantChange,
    handleSectionBarFromPeriodChange,
    handleSectionBarToPeriodChange,
    handleSectionBarDirectionChange,
    handleExport,
    isGeneratingReport,
  ]);

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column' }} data-testid='e55-comparison-container'>
      <Grid container>
        {/* First Row - SectionBar */}
        <Grid item xs={12}>
                      <SectionBar
              title='eNO1-e55 comparison'
              subtitle='eNO1 Sampling'
            onToggleSidebar={handleSidebarToggle}
            isSidebarOpen={sidebarOpen}
            onSubtitleClick={handleSubtitleClick}
            showFilters={{
              ppo: !!htmlResults,
              customE55: !!htmlResults,
            }}
            customFilters={customFiltersForSectionBar}
            data-testid='section-bar'
          />
        </Grid>

        {/* Second Row - Sidebar and MainContent */}
        <Grid item xs={12} sx={{ display: 'flex', position: 'relative' }}>
          {/* Sidebar */}
          {sidebarOpen && (
            <Box sx={{ position: 'relative' }}>
              <Sidebar isOpen={sidebarOpen} onToggle={handleSidebarToggle} navItems={navItems} data-testid="e55-comparison-sidebar" />
            </Box>
          )}

          {/* Main Content */}
          <MainContent sidebarOpen={sidebarOpen} data-testid='e55-comparison-main-content'>
            <ReportContainer data-testid="e55-comparison-content">
              {/* Header */}
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  mb: 3,
                }}
                data-testid="e55-comparison-header"
              >
                                  <Typography variant='h4' sx={{ fontWeight: 600, color: '#063142' }} data-testid="e55-comparison-title">
                    eNO1-e55 comparison
                  </Typography>
                <Box sx={{ display: 'flex', gap: 1 }} data-testid="e55-comparison-header-actions">
                  {htmlResults && (
                    <ClearResultsButton variant='outlined' onClick={clearResults} data-testid="e55-comparison-clear-results-btn">
                      Clear Results
                    </ClearResultsButton>
                  )}
                </Box>
              </Box>

              {/* Main Filter Section - Hide when HTML results exist */}
              {!htmlResults && (
                <>
                  {/* PPO Section */}
                  <FilterSection data-testid="e55-comparison-filters">
                    {/* PPO Dropdown and Direction Filter in one row */}
                    <Box sx={{ display: 'flex', alignItems: 'flex-end', gap: 1.5, mb: 2 }} data-testid="e55-comparison-ppo-direction-section">
                      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }} data-testid="e55-comparison-ppo-section">
                        <Typography sx={{ fontSize: '14px', fontWeight: 700, color: '#063142' }}>
                          DO *
                        </Typography>
                        <SelectContainer>
                          <FormControl size='small' fullWidth>
                            <Select
                              value={ppoValue}
                              onChange={(e) => handleDOChange(e.target.value)}
                              displayEmpty
                              sx={{ minWidth: 160 }}
                              disabled={isPpoLoading}
                              data-testid="e55-comparison-ppo-select"
                            >
                              <MenuItem value='' disabled>
                                {isPpoLoading ? (
                                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                    <CircularProgress size={16} />
                                    <span>Loading...</span>
                                  </Box>
                                ) : (
                                  'Select DO'
                                )}
                              </MenuItem>
                              {!isPpoLoading &&
                                !ppoError &&
                                ppoOptions.map((option) => (
                                  <MenuItem key={option.id} value={option.id}>
                                    <Box
                                      sx={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'space-between',
                                        width: '100%',
                                      }}
                                    >
                                      <span>{option.label}</span>
                                    </Box>
                                  </MenuItem>
                                ))}
                              {ppoError && <MenuItem disabled>Error loading PPOs</MenuItem>}
                            </Select>
                          </FormControl>
                        </SelectContainer>
                      </Box>

                      {/* Direction Filter using Segmented Control */}
                      <StyledSegmentedControl data-testid="e55-comparison-direction-filter">
                        {directionOptions.map((option) => (
                          <StyledSegmentButton
                            key={option.id}
                            active={ppoFilter === option.id}
                            onClick={() => handleDirectionChange(option.id)}
                            sx={{
                              color: ppoFilter === option.id ? '#ffffff' : '#00B6BA',
                              minWidth: '70px',
                            }}
                          >
                            {option.label}
                          </StyledSegmentButton>
                        ))}
                      </StyledSegmentedControl>
                    </Box>

                    {/* Divider */}
                    <Box
                      sx={{
                        height: '1px',
                        backgroundColor: '#E1E8ED',
                        mb: 2,
                      }}
                    />

                    {/* Date Selectors with titles */}
                    <Box sx={{ display: 'flex', gap: 2 }}>
                      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                        <Typography sx={{ fontSize: '14px', fontWeight: 700, color: '#063142' }}>
                          From YYYY.QQ: *
                        </Typography>
                        <SelectContainer>
                          <FormControl size='small' fullWidth>
                            <Select
                              value={getFromValue()}
                              onChange={(e) => handleFromChangeWithCascading(e.target.value)}
                              displayEmpty
                              sx={{ minWidth: 184 }}
                            >
                              <MenuItem value=''>Select</MenuItem>
                              {yearQuarterOptions.map((option) => (
                                <MenuItem key={option.value} value={option.value}>
                                  {option.label}
                                </MenuItem>
                              ))}
                            </Select>
                          </FormControl>
                        </SelectContainer>
                      </Box>

                      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                        <Typography sx={{ fontSize: '14px', fontWeight: 700, color: '#063142' }}>
                          To YYYY.QQ: *
                        </Typography>
                        <SelectContainer>
                          <FormControl size='small' fullWidth>
                            <Select
                              value={getToValue()}
                              onChange={(e) => handleToChange(e.target.value)}
                              displayEmpty
                              sx={{ minWidth: 184 }}
                            >
                              <MenuItem value=''>Select</MenuItem>
                              {yearQuarterOptions.map((option) => (
                                <MenuItem key={option.value} value={option.value}>
                                  {option.label}
                                </MenuItem>
                              ))}
                            </Select>
                          </FormControl>
                        </SelectContainer>
                      </Box>
                    </Box>
                  </FilterSection>

                  {/* Main Content Grid - MSC Checkboxes */}
                  <Box
                    sx={{
                      maxWidth: '1000px',
                      width: '100%',
                    }}
                  >
                    {/* Main Section Title */}
                    <Box sx={{ mb: 3 , ml: 2}}>
                      <SectionTitle>Included MSCs from e55</SectionTitle>
                    </Box>

                    {/* MSC Checkboxes Grid */}
                    <Box
                      sx={{
                        display: 'flex',
                        flexDirection: { xs: 'column', md: 'row' },
                        width: '100%',
                      }}
                    >
                      {/* Sampling Relevant MSCs */}
                      <Box
                        sx={{
                          flex: { xs: 'none', md: '1 1 0' },
                          padding: 2,
                          minWidth: 0,
                        }}
                      >
                        <Typography
                          sx={{
                            fontSize: '12px',
                            fontWeight: 600,
                            color: '#00ACE7',
                            mb: 1,
                          }}
                        >
                          Sampling relevant:
                        </Typography>
                        <Box
                          sx={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center',
                            mb: 1,
                          }}
                        >
                          <Box></Box>
                          <Link
                            component='button'
                            onClick={handleSelectAllSamplingRelevant}
                            sx={{
                              color: '#00ACE7',
                              textDecoration: 'none',
                              fontSize: '12px',
                              fontWeight: 500,
                              padding: '4px 8px',
                              borderRadius: '4px',
                              transition: 'all 0.2s ease',
                              '&:hover': {
                                backgroundColor: '#E3F7FE',
                                color: '#0095C7',
                                textDecoration: 'none',
                              },
                              '&:active': {
                                backgroundColor: '#B3E5FC',
                              },
                            }}
                          >
                            {getSamplingRelevantButtonText()}
                          </Link>
                        </Box>
                        <CheckboxGroup>
                          <CustomCheckbox
                            checked={samplingRelevant.UA}
                            onChange={handleSamplingRelevantChange('UA')}
                            label='UA'
                          />
                          <CustomCheckbox
                            checked={samplingRelevant.UE}
                            onChange={handleSamplingRelevantChange('UE')}
                            label='UE'
                          />
                          <CustomCheckbox
                            checked={samplingRelevant.UL}
                            onChange={handleSamplingRelevantChange('UL')}
                            label='UL'
                          />
                          <CustomCheckbox
                            checked={samplingRelevant.UN}
                            onChange={handleSamplingRelevantChange('UN')}
                            label='UN'
                          />
                          <CustomCheckbox
                            checked={samplingRelevant.UY}
                            onChange={handleSamplingRelevantChange('UY')}
                            label='UY'
                          />
                        </CheckboxGroup>
                      </Box>

                      {/* Vertical Divider */}
                      <Box
                        sx={{
                          width: '1px',
                          backgroundColor: '#E1E8ED',
                          display: { xs: 'none', md: 'block' },
                        }}
                      />

                      {/* Horizontal Divider for mobile */}
                      <Box
                        sx={{
                          height: '1px',
                          backgroundColor: '#E1E8ED',
                          my: 2,
                          display: { xs: 'block', md: 'none' },
                        }}
                      />

                      {/* Not Sampling Relevant MSCs */}
                      <Box
                        sx={{
                          flex: { xs: 'none', md: '1 1 0' },
                          padding: 2,
                          minWidth: 0,
                        }}
                      >
                        <Typography
                          sx={{
                            fontSize: '12px',
                            fontWeight: 600,
                            color: '#FF6B35',
                            mb: 1,
                          }}
                        >
                          Not sampling relevant:
                        </Typography>
                        <Box
                          sx={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center',
                            mb: 1,
                          }}
                        >
                          <Box></Box>
                          <Link
                            component='button'
                            onClick={handleSelectAllNotSamplingRelevant}
                            sx={{
                              color: '#00ACE7',
                              textDecoration: 'none',
                              fontSize: '12px',
                              fontWeight: 500,
                              padding: '4px 8px',
                              borderRadius: '4px',
                              transition: 'all 0.2s ease',
                              '&:hover': {
                                backgroundColor: '#E3F7FE',
                                color: '#0095C7',
                                textDecoration: 'none',
                              },
                              '&:active': {
                                backgroundColor: '#B3E5FC',
                              },
                            }}
                          >
                            {getNotSamplingRelevantButtonText()}
                          </Link>
                        </Box>
                        <CheckboxGroup>
                          <CustomCheckbox
                            checked={notSamplingRelevant.UI}
                            onChange={handleNotSamplingRelevantChange('UI')}
                            label='UI'
                          />
                          <CustomCheckbox
                            checked={notSamplingRelevant.UM}
                            onChange={handleNotSamplingRelevantChange('UM')}
                            label='UM'
                          />
                          <CustomCheckbox
                            checked={notSamplingRelevant.UP}
                            onChange={handleNotSamplingRelevantChange('UP')}
                            label='UP'
                          />
                          <CustomCheckbox
                            checked={notSamplingRelevant.UR}
                            onChange={handleNotSamplingRelevantChange('UR')}
                            label='UR'
                          />
                          <CustomCheckbox
                            checked={notSamplingRelevant.UX}
                            onChange={handleNotSamplingRelevantChange('UX')}
                            label='UX'
                          />
                          <CustomCheckbox
                            checked={notSamplingRelevant.UZ}
                            onChange={handleNotSamplingRelevantChange('UZ')}
                            label='UZ'
                          />
                        </CheckboxGroup>
                      </Box>
                    </Box>
                  </Box>

                  {/* Action Buttons */}
                  <ButtonContainer sx={{ justifyContent: 'flex-end' }}>
                    <ResetButton variant='contained' onClick={handleReset}>
                      RESET
                    </ResetButton>
                    <ExportButton
                      variant='contained'
                      onClick={() => handleExport('HTML')}
                      disabled={isGeneratingReport}
                    >
                      {isGeneratingReport ? <CircularProgress size={16} /> : 'HTML'}
                    </ExportButton>
                    <ExportButton
                      variant='contained'
                      onClick={() => handleExport('XLS')}
                      disabled={isGeneratingReport}
                    >
                      {isGeneratingReport ? <CircularProgress size={16} /> : 'XLS'}
                    </ExportButton>
                  </ButtonContainer>
                </>
              )}

              {/* Results Section - Show table directly under filters */}
              {htmlResults && (
                <SortedE55ComparisonTable 
                  data={htmlResults}
                  direction={resultsDirection}
                />
              )}

              {/* No Data Available Message */}
              {hasEmptyResults && (
                <NoDataFound
                  title="No Data Available"
                  message="No e55 comparison data was found for the selected criteria. Please try adjusting your filters or selecting a different date range."
                  onClearResults={clearResults}
                  onResetFilters={handleReset}
                />
              )}
            </ReportContainer>
          </MainContent>
        </Grid>
      </Grid>

      {/* Notification Snackbar */}
      <NotificationSnackbar
        open={snackbar.open}
        message={snackbar.message}
        severity={snackbar.severity}
        onClose={handleSnackbarClose}
        data-testid='e55-comparison-snackbar'
      />
    </Box>
  );
}

export default E55Comparison;
